/**
 * LanguagePickerModal — Searchable modal for selecting a language.
 * Shows flag + native name + English name in a scrollable FlatList.
 * Search filters by native name, English name, or language code.
 */

import React, { useState, useMemo, useCallback, useId } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  Modal,
  StyleSheet,
  SafeAreaView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, type ThemeColors } from '../utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../utils/design-tokens';
import { type Language, LANG_ENGLISH_NAMES } from '../utils/constants';
import { useBackHandler } from '../navigation/useBackHandler';

interface LanguagePickerModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (code: string) => void;
  selectedCode: string;
  languages: Language[];
  title?: string;
}

const POPULAR_LANG_CODES = ['en', 'ar', 'es', 'fr', 'de', 'zh', 'pt', 'ru', 'ja', 'hi', 'ko', 'it', 'tr', 'nl'];

export default function LanguagePickerModal({
  visible,
  onClose,
  onSelect,
  selectedCode,
  languages,
  title = 'Select Language',
}: LanguagePickerModalProps) {
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const modalId = useId();
  const [search, setSearch] = useState('');

  const popularLanguages = useMemo(
    () => POPULAR_LANG_CODES
      .map((code) => languages.find((l) => l.code === code))
      .filter(Boolean) as Language[],
    [languages],
  );

  const allNonPopular = useMemo(
    () => languages
      .filter((l) => !POPULAR_LANG_CODES.includes(l.code))
      .sort((a, b) => {
        const aName = LANG_ENGLISH_NAMES[a.code] || a.label;
        const bName = LANG_ENGLISH_NAMES[b.code] || b.label;
        return aName.localeCompare(bName);
      }),
    [languages],
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return languages
      .filter(
        (l) =>
          l.label.toLowerCase().includes(q) ||
          (LANG_ENGLISH_NAMES[l.code] || '').toLowerCase().includes(q) ||
          l.code.toLowerCase() === q,
      )
      .sort((a, b) => {
        const aName = LANG_ENGLISH_NAMES[a.code] || a.label;
        const bName = LANG_ENGLISH_NAMES[b.code] || b.label;
        return aName.localeCompare(bName);
      });
  }, [search, languages]);

  const isSearching = !!search.trim();

  const handleSelect = useCallback(
    (code: string) => {
      onSelect(code);
      onClose();
      setSearch('');
    },
    [onSelect, onClose],
  );

  const handleClose = useCallback(() => {
    onClose();
    setSearch('');
  }, [onClose]);

  useBackHandler({
    id: `language-picker-${modalId}`,
    label: title,
    priority: 950,
    enabled: visible,
    onBack: () => {
      handleClose();
      return true;
    },
  });

  const renderItem = useCallback(
    ({ item }: { item: Language }) => {
      const englishName = LANG_ENGLISH_NAMES[item.code];
      const isSelected = item.code === selectedCode;
      return (
        <TouchableOpacity
          style={[s.row, isSelected && s.rowSelected]}
          onPress={() => handleSelect(item.code)}
          activeOpacity={0.6}
        >
          <Text style={s.flag}>{item.flag}</Text>
          <View style={s.nameContainer}>
            <Text style={[s.nativeName, isSelected && s.nameSelected]} numberOfLines={1}>
              {item.label}
            </Text>
            {englishName && englishName !== item.label && (
              <Text style={s.englishName} numberOfLines={1}>
                {englishName}
              </Text>
            )}
          </View>
          {isSelected && (
            <Ionicons name="checkmark-circle" size={22} color={colors.primary} />
          )}
        </TouchableOpacity>
      );
    },
    [handleSelect, selectedCode, colors, s],
  );

  const keyExtractor = useCallback((item: Language) => item.code, []);

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={handleClose}>
      <SafeAreaView style={s.modal}>
        {/* Header */}
        <View style={s.header}>
          <Text style={s.headerTitle}>{title}</Text>
          <TouchableOpacity onPress={handleClose} accessibilityRole="button" accessibilityLabel="Close">
            <Ionicons name="close" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>

        {/* Search */}
        <View style={s.searchContainer}>
          <Ionicons name="search" size={18} color={colors.textSecondary} style={s.searchIcon} />
          <TextInput
            style={s.searchInput}
            value={search}
            onChangeText={setSearch}
            placeholder="Search language..."
            placeholderTextColor={colors.textSecondary}
            autoFocus={Platform.OS !== 'web'}
            autoCorrect={false}
            autoCapitalize="none"
          />
          {search.length > 0 && (
            <TouchableOpacity onPress={() => setSearch('')} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="close-circle" size={18} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>

        {isSearching ? (
          <FlatList
            data={filtered}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            ListEmptyComponent={
              <Text style={s.empty}>No languages found</Text>
            }
          />
        ) : (
          <FlatList
            data={allNonPopular}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            ListHeaderComponent={
              <>
                <Text style={s.sectionLabel}>Popular</Text>
                {popularLanguages.map((item) => {
                  const englishName = LANG_ENGLISH_NAMES[item.code];
                  const isSelected = item.code === selectedCode;
                  return (
                    <TouchableOpacity
                      key={item.code}
                      style={[s.row, isSelected && s.rowSelected]}
                      onPress={() => handleSelect(item.code)}
                      activeOpacity={0.6}
                    >
                      <Text style={s.flag}>{item.flag}</Text>
                      <View style={s.nameContainer}>
                        <Text style={[s.nativeName, isSelected && s.nameSelected]} numberOfLines={1}>
                          {item.label}
                        </Text>
                        {englishName && englishName !== item.label && (
                          <Text style={s.englishName} numberOfLines={1}>
                            {englishName}
                          </Text>
                        )}
                      </View>
                      {isSelected && (
                        <Ionicons name="checkmark-circle" size={22} color={colors.primary} />
                      )}
                    </TouchableOpacity>
                  );
                })}
                <Text style={s.sectionLabel}>All Languages</Text>
              </>
            }
          />
        )}
      </SafeAreaView>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  modal: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.base,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    fontSize: FONT_SIZE.md,
    fontWeight: '700',
    color: colors.text,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: RADIUS.md,
    marginHorizontal: SPACING.lg,
    marginVertical: SPACING.md,
    paddingHorizontal: SPACING.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchIcon: {
    marginRight: SPACING.sm,
  },
  searchInput: {
    flex: 1,
    paddingVertical: SPACING.md,
    fontSize: FONT_SIZE.base,
    color: colors.text,
  },
  sectionLabel: {
    fontSize: FONT_SIZE.sm,
    fontWeight: '600',
    color: colors.textSecondary,
    marginHorizontal: SPACING.lg,
    marginTop: SPACING.md,
    marginBottom: SPACING.xs,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  rowSelected: {
    backgroundColor: colors.primaryLight,
  },
  flag: {
    fontSize: 24,
    marginRight: SPACING.md,
    width: 32,
    textAlign: 'center',
  },
  nameContainer: {
    flex: 1,
    marginRight: SPACING.sm,
  },
  nativeName: {
    fontSize: FONT_SIZE.base,
    color: colors.text,
    fontWeight: '500',
  },
  nameSelected: {
    color: colors.primary,
    fontWeight: '600',
  },
  englishName: {
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    marginTop: 1,
  },
  empty: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: SPACING['3xl'],
    fontSize: FONT_SIZE.base,
  },
});
